import { ReactNode, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import Collapse from '@material-ui/core/Collapse';
import { Link, useSidebarOpenState } from '@backstage/core-components';
import { useLocation } from 'react-router-dom';
import { isActivePath } from './activePath';
import { ChevronDownIcon } from './navIcons';
import { SHELF_ROW, useShelfFilter } from './SidebarShelf';

/**
 * A navigation group whose children open INSIDE the sidebar (DX-275).
 *
 * Backstage's own `SidebarSubmenu` is a flyout: `position: fixed`, opened on
 * hover, drawn on top of the page. On a wide screen it covers half the content
 * a reader was looking at, and it disappears the moment the pointer leaves —
 * so a reader cannot keep the section open while they read.
 *
 * Every other revenexx surface does the opposite. On revenexx.dev a section
 * like "Build a revenexx App" expands in place and its children sit indented
 * underneath, so the tree stays visible and the content is never covered.
 * This is that behaviour: a disclosure, not an overlay.
 *
 * It opens by itself when the reader is already somewhere inside it, which is
 * what makes the tree answer "where am I" and not just "what is there".
 *
 * MUI v4 `makeStyles` only — no MUI v5.
 */

export type SectionChild = {
  title: string;
  subtitle?: string;
  to: string;
};

const useStyles = makeStyles({
  /*
    Drawn as a row of SidebarNavItem (DX-352) — the same geometry and tones,
    from the Developer Portal's `.docs-sidebar-link` — so the one entry that
    opens reads as one of the list rather than a control of its own.
  */
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.625rem',
    width: 'calc(100% - 1rem)',
    margin: '0 0.5rem 2px',
    padding: '0.375rem 0.5rem',
    boxSizing: 'border-box',
    border: 'none',
    borderRadius: '0.375rem',
    background: 'none',
    color: 'var(--ui-nav-link)',
    // `font` is a shorthand and resets weight along with everything else, so
    // the properties below have to follow it rather than precede it.
    font: 'inherit',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    fontWeight: 400,
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'background-color 160ms ease, color 160ms ease',
    '&:hover': {
      background: 'var(--ui-nav-hover-bg)',
      color: 'var(--ui-nav-link-hover)',
    },
    '&:hover $icon': {
      color: 'var(--ui-nav-icon-active)',
    },
  },
  headerClosed: {
    justifyContent: 'center',
    padding: '0.5rem 0',
  },
  icon: {
    display: 'flex',
    flex: '0 0 auto',
    color: 'var(--ui-nav-icon)',
    transition: 'color 160ms ease',
  },
  label: {
    flex: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  chevron: {
    display: 'flex',
    flex: '0 0 auto',
    color: 'var(--ui-nav-chevron)',
    transition: 'transform 160ms ease',
  },
  chevronOpen: {
    transform: 'rotate(180deg)',
  },
  /*
    The portals' `.docs-sidebar-link.depth-1`: no icon, and indented by the
    parent's icon column (0.5rem padding + 0.875rem icon + 0.625rem gap =
    2rem), so a child's label starts under its parent's label.
  */
  child: {
    display: 'block',
    width: 'calc(100% - 1rem)',
    margin: '0 0.5rem 2px',
    padding: '0.375rem 0.5rem 0.375rem 2rem',
    boxSizing: 'border-box',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    textDecoration: 'none',
    color: 'var(--ui-nav-link-nested)',
    transition: 'background-color 160ms ease, color 160ms ease',
    '&:hover': {
      background: 'var(--ui-nav-hover-bg)',
      color: 'var(--ui-nav-link-hover)',
      textDecoration: 'none',
    },
  },
  childActive: {
    background: 'var(--ui-nav-active-bg)',
    color: 'var(--ui-nav-link-active)',
    fontWeight: 500,
    '&:hover': {
      background: 'var(--ui-nav-active-bg)',
      color: 'var(--ui-nav-link-active)',
    },
  },
  childSubtitle: {
    display: 'block',
    fontSize: '0.75rem',
    lineHeight: '1rem',
    fontWeight: 400,
    opacity: 0.7,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // The drawer lays its children out with `align-items: flex-start`, so the
  // wrapper would shrink to its widest child and the rows' hover fill with it.
  collapse: {
    width: '100%',
  },
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
});

export const SidebarSection = ({
  text,
  icon,
  children,
}: {
  text: string;
  icon: ReactNode;
  children: SectionChild[];
}) => {
  const classes = useStyles();
  const { pathname, search } = useLocation();
  const { isOpen } = useSidebarOpenState();
  const { filtering, matches } = useShelfFilter();

  // A section the reader is already inside opens by itself — otherwise the
  // tree would hide the very entry that is currently showing.
  /*
    Compared on path AND filter, not on path alone: five of the catalog's six
    children are /catalog with different filters, so a bare prefix match marks
    all of them at once and none of them correctly.
  */
  const sameEntry = (to: string) => {
    const [path, query] = to.split('?');
    if (!isActivePath(pathname, path)) {
      return false;
    }
    // A child with no filter matches the bare path only.
    return query ? search.includes(query) : !search;
  };
  const containsCurrent = children.some(c =>
    isActivePath(pathname, c.to.split('?')[0]),
  );

  /**
   * What the reader last decided, or `undefined` while they have not decided
   * anything. Kept apart from `containsCurrent` on purpose: a single boolean
   * cannot tell "closed because the reader closed it" from "closed because
   * the current page happens to be elsewhere", and conflating the two is what
   * made the section collapse the moment somebody clicked Docs or JAM. The
   * reader's choice outlives navigation, which is how the other revenexx
   * portals behave — a section they opened stays open while they read.
   */
  const [choice, setChoice] = useState<boolean | undefined>(undefined);
  const open = choice ?? containsCurrent;
  const setOpen = (next: boolean | ((v: boolean) => boolean)) =>
    setChoice(typeof next === 'function' ? next(open) : next);

  const renderChild = (child: SectionChild) => (
    <Link
      key={child.to}
      to={child.to}
      underline="none"
      {...SHELF_ROW}
      className={`${classes.child} ${
        sameEntry(child.to) ? classes.childActive : ''
      }`}
    >
      {child.title}
      {child.subtitle && (
        <span className={classes.childSubtitle}>{child.subtitle}</span>
      )}
    </Link>
  );

  /*
    Under a query the section stands aside for whatever of it matches: its own
    name brings the whole section, a child's name or subtitle brings that child
    alone, flat among the other matches — the way the portals flatten theirs.
  */
  if (filtering && !matches(text)) {
    return (
      <>{children.filter(c => matches(c.title, c.subtitle)).map(renderChild)}</>
    );
  }

  return (
    <>
      <button
        type="button"
        {...SHELF_ROW}
        className={`${classes.header} ${isOpen ? '' : classes.headerClosed}`}
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
      >
        <span className={classes.icon}>{icon}</span>
        <span className={isOpen ? classes.label : classes.visuallyHidden}>
          {text}
        </span>
        {isOpen && (
          <span
            className={`${classes.chevron} ${open ? classes.chevronOpen : ''}`}
          >
            <ChevronDownIcon />
          </span>
        )}
      </button>

      {/* Collapsed to its icons the column has no room for the children's
          names, so they wait until it opens again. */}
      <Collapse
        in={open && isOpen}
        timeout="auto"
        unmountOnExit
        className={classes.collapse}
      >
        {children.map(renderChild)}
      </Collapse>
    </>
  );
};
